import { useEffect, useState } from 'react';
import { Organization } from '../types/organization';
import { useOrganizations } from './useOrganizations';
import { useOrgSyncContext } from '../context/OrgSyncProvider';

interface UseSelectedOrganizationReturn {
  selectedOrganization: { orgId: number | null; orgName: string | null };
  setSelectedOrganization: (org: Organization | null) => void;
  isLoaded: boolean;
  isInitialized: boolean;
}

/**
 * Хук для управления состоянием выбранной организации
 * Использует библиотеку @osnova/org-sync для синхронизации
 */
export const useSelectedOrganization = (): UseSelectedOrganizationReturn => {
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Получаем данные из org-sync контекста
  const { currentOrg, setOrganization, isLoaded } = useOrgSyncContext();
  
  // Загружаем список доступных организаций
  const { data: organizations = [], isSuccess: orgsLoaded } = useOrganizations();

  // Логика выбора по умолчанию: сохраненная → первая → placeholder
  useEffect(() => {
    if (!isLoaded || !orgsLoaded || isInitialized) return;

    console.log('🎯 [useSelectedOrganization] Running default selection logic:', {
      isLoaded,
      orgsLoaded,
      isInitialized,
      organizationsCount: organizations.length,
      currentOrg
    });

    // Проверяем, есть ли сохранённая организация в списке доступных
    const savedOrgExists = currentOrg?.id && organizations.some(
      org => org.organization_id === currentOrg.id
    );

    if (savedOrgExists) {
      console.log('✅ [useSelectedOrganization] Saved organization is valid');
      // Сохранённая организация валидна - ничего не делаем, org-sync уже отправил событие
    } else if (organizations.length > 0) {
      console.log('🔄 [useSelectedOrganization] No saved org, selecting first available:', organizations[0]);
      // Выбираем первую доступную организацию
      const firstOrg = organizations[0];
      setOrganization({
        id: firstOrg.organization_id,
        name: firstOrg.organization_name
      });
    } else {
      console.log('❌ [useSelectedOrganization] No organizations available, clearing selection');
      // Список пуст - очищаем выбор
      setOrganization(null);
    }

    setIsInitialized(true);
    console.log('✅ [useSelectedOrganization] Default selection logic completed');
  }, [isLoaded, orgsLoaded, organizations, currentOrg, isInitialized, setOrganization]);

  // Функция для установки выбранной организации
  const setSelectedOrganization = (org: Organization | null) => {
    console.log('🎯 [useSelectedOrganization] Setting selected organization:', org);

    if (org) {
      setOrganization({
        id: org.organization_id,
        name: org.organization_name
      });
    } else {
      setOrganization(null);
    }
    
    console.log('✅ [useSelectedOrganization] Organization set successfully');
  };

  return {
    selectedOrganization: {
      orgId: currentOrg?.id ?? null,
      orgName: currentOrg?.name ?? null
    },
    setSelectedOrganization,
    isLoaded,
    isInitialized,
  };
}; 